import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Flame, Coins } from "lucide-react";
import { useWalletUser, useApp, ConnectPrompt } from "@/App";
import { spinWheel } from "@/api";

export default function SpinPage() {
  const { isConnected, user, address, refresh } = useWalletUser();
  const { config, showToast } = useApp();
  const [wager, setWager] = useState(50);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const spinningRef = useRef(false);

  if (!isConnected) return <ConnectPrompt />;
  if (!config) return <div className="pt-32 text-center text-slate-400">Loading...</div>;

  const segs = config.wheel;
  const total = segs.length;
  const segAngle = 360 / total;

  const doSpin = async () => {
    if (spinningRef.current) return;
    spinningRef.current = true;
    setSpinning(true);
    setLastResult(null);
    try {
      const r = await spinWheel(address, Number(wager));
      // target angle: pointer at top; segment i center at i*segAngle. We want to land on it.
      const targetDeg = 360 * 6 + (360 - (r.segment_index * segAngle + segAngle / 2));
      setRotation((prev) => prev + targetDeg);
      setTimeout(() => {
        setLastResult(r);
        showToast(r.flame_won > 0 ? `🎉 ${r.segment.label} — +${r.flame_won} FLAME!` : `${r.segment.label} — better luck next spin`);
        refresh();
        setSpinning(false);
        spinningRef.current = false;
      }, 5100);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Spin failed", "error");
      setSpinning(false);
      spinningRef.current = false;
    }
  };

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="font-display text-4xl font-black">Lucky Spin</h1>
        <p className="text-slate-400 mt-1">Credits: <span className="text-cyan-400 font-mono">{user?.credits ?? 0}</span> • FLAME: <span className="text-orange-400 font-mono">{user?.flame ?? 0}</span></p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="relative aspect-square w-full max-w-md mx-auto" data-testid="game-lucky-spin-wheel">
            <div className="absolute left-1/2 -translate-x-1/2 -top-3 z-10 text-3xl">▼</div>
            <svg viewBox="0 0 200 200" className="wheel w-full h-full" style={{ transform: `rotate(${rotation}deg)` }}>
              {segs.map((s, i) => {
                const start = (i * segAngle - 90) * (Math.PI / 180);
                const end = ((i + 1) * segAngle - 90) * (Math.PI / 180);
                const x1 = 100 + 100 * Math.cos(start);
                const y1 = 100 + 100 * Math.sin(start);
                const x2 = 100 + 100 * Math.cos(end);
                const y2 = 100 + 100 * Math.sin(end);
                const largeArc = segAngle > 180 ? 1 : 0;
                const d = `M100,100 L${x1},${y1} A100,100 0 ${largeArc} 1 ${x2},${y2} Z`;
                const midAngle = (i * segAngle + segAngle / 2 - 90) * (Math.PI / 180);
                const tx = 100 + 60 * Math.cos(midAngle);
                const ty = 100 + 60 * Math.sin(midAngle);
                return (
                  <g key={i}>
                    <path d={d} fill={s.color} stroke="#0F101A" strokeWidth="1" />
                    <text x={tx} y={ty} fill="#fff" fontSize="9" fontWeight="800" textAnchor="middle"
                      transform={`rotate(${i * segAngle + segAngle / 2}, ${tx}, ${ty})`}>{s.label}</text>
                  </g>
                );
              })}
              <circle cx="100" cy="100" r="18" fill="#FF5500" stroke="#fff" strokeWidth="2" />
            </svg>
          </div>

          <div className="card-tile card-flame">
            <h3 className="font-display text-xl font-bold">Place your wager</h3>
            <div className="mt-3 flex items-center gap-2">
              <Coins size={16} className="text-cyan-400" />
              <input data-testid="game-spin-wager-input" type="number" min="10" step="10" value={wager}
                onChange={(e) => setWager(Number(e.target.value))}
                className="bg-[#12131C] border border-[#2A2E45] rounded-xl px-4 py-2 w-32 font-mono" />
              <span className="text-xs text-slate-400">credits</span>
            </div>
            <button data-testid="game-spin-button" onClick={doSpin} disabled={spinning || !user || user.credits < wager}
              className="btn-tactile btn-flame mt-4 w-full font-display text-lg">
              {spinning ? "Spinning..." : "SPIN THE WHEEL"}
            </button>
            {lastResult && (
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mt-4 p-3 rounded-xl bg-black/40 border border-orange-500/30">
                <div className="font-display text-2xl font-black text-orange-400">{lastResult.segment.label}</div>
                {lastResult.flame_won > 0 ? (
                  <div className="text-sm">You won <span className="text-amber-400 font-bold">+{lastResult.flame_won} FLAME</span> 🔥</div>
                ) : (
                  <div className="text-sm text-slate-400">Burned to the ashes.</div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
